/**
 * Normaliza o texto cru de uma mensagem para markdown antes de entregá-lo ao
 * markdown-to-jsx.
 *
 * O markdown-to-jsx casa delimitadores muito mais soltos que o WhatsApp: em
 * "ALTER TABLE efd_k235 ADD COLUMN `QTD_ENC` ..." ele emparelha o "_" de
 * efd_k235 com o de QTD_ENC, come os dois underscores e ainda quebra a crase.
 * Aqui decidimos, com as mesmas regras de borda do WhatsApp, quais
 * delimitadores são formatação de verdade; todo o resto é escapado com "\"
 * (regra textEscaped do markdown-to-jsx), portanto reaparece literal na tela.
 */

// Mesma regra de code span usada pelo markdown-to-jsx (aceita N crases).
const CODE_SPAN_REGEX = /(`+)((?:\\`|(?!\1)`|[^`])+)\1/g;
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"')\]]+/gi;
const MONOSPACE_BLOCK_REGEX = /```([\s\S]*?)```/g;
const ALPHANUMERIC_REGEX = /[\p{L}\p{N}]/u;
const WHITESPACE_REGEX = /\s/;
const ESCAPABLE_REGEX = /[\\*_~`]/;

// Marcador dos trechos protegidos (crases e URLs). NUL não aparece em mensagem
// real e não é alfanumérico, então não interfere nas regras de borda.
const MARK = String.fromCharCode(0);
const MARK_REGEX = new RegExp(MARK, "g");
const PLACEHOLDER_REGEX = new RegExp(`${MARK}(\\d+)${MARK}`, "g");

const MARKDOWN_BY_DELIMITER = { "*": "**", _: "_", "~": "~~" };
// "**" e "~~" já são markdown pronto (texto gerado por IA); "__" não é itálico
// no WhatsApp, então o underscore só vale como delimitador simples.
const DOUBLE_DELIMITERS = ["*", "~"];
// Formatação aninhada no WhatsApp tem no máximo 3 níveis reais (*_~texto~_*);
// o limite evita estouro de pilha com texto patológico cheio de delimitadores.
const MAX_NESTING = 6;

const isAlphanumeric = char => Boolean(char) && ALPHANUMERIC_REGEX.test(char);
const isWhitespace = char => Boolean(char) && WHITESPACE_REGEX.test(char);

/**
 * A validade de um fechamento depende só da própria posição, nunca de onde a
 * marcação abriu. Por isso mapeamos todas as posições uma única vez por texto e
 * depois localizamos o fechamento por busca binária — sem isso, uma mensagem
 * cheia de "_" sem par faria uma varredura completa a cada abertura.
 */
const collectClosers = text => {
	const closers = {};
	Object.keys(MARKDOWN_BY_DELIMITER).forEach(delimiter => {
		closers[delimiter] = { 1: [], 2: [] };
	});

	for (let index = 0; index < text.length; index += 1) {
		const char = text[index];
		const buckets = closers[char];

		if (!buckets) continue;
		// "texto *" não fecha nada: o WhatsApp exige conteúdo colado ao delimitador.
		if (isWhitespace(text[index - 1])) continue;

		if (!isAlphanumeric(text[index + 1])) {
			buckets[1].push(index);
		}
		if (text[index + 1] === char && !isAlphanumeric(text[index + 2])) {
			buckets[2].push(index);
		}
	}

	return closers;
};

const findCloser = (positions, contentStart, limit, run) => {
	let low = 0;
	let high = positions.length - 1;
	let found = -1;

	// Primeira posição estritamente maior que contentStart (conteúdo não vazio).
	while (low <= high) {
		const middle = (low + high) >> 1;

		if (positions[middle] > contentStart) {
			found = positions[middle];
			high = middle - 1;
		} else {
			low = middle + 1;
		}
	}

	return found !== -1 && found + run <= limit ? found : -1;
};

const convert = (text, start, end, closers, depth) => {
	let output = "";
	let index = start;

	while (index < end) {
		const char = text[index];
		const markdown = MARKDOWN_BY_DELIMITER[char];

		if (markdown && depth < MAX_NESTING) {
			const run = DOUBLE_DELIMITERS.includes(char) && text[index + 1] === char ? 2 : 1;
			const contentStart = index + run;
			const opens =
				!isAlphanumeric(text[index - 1]) &&
				contentStart < end &&
				!isWhitespace(text[contentStart]) &&
				text[contentStart] !== char;

			if (opens) {
				const closer = findCloser(closers[char][run], contentStart, end, run);

				if (closer !== -1) {
					output += markdown + convert(text, contentStart, closer, closers, depth + 1) + markdown;
					index = closer + run;
					continue;
				}
			}
		}

		output += ESCAPABLE_REGEX.test(char) ? `\\${char}` : char;
		index += 1;
	}

	return output;
};

export const whatsappToMarkdown = value => {
	const text = value === null || value === undefined ? "" : String(value);

	if (!text) return "";

	const atoms = [];
	const hold = match => {
		atoms.push(match);
		return `${MARK}${atoms.length - 1}${MARK}`;
	};

	// Crases e URLs viram átomos opacos antes de qualquer emparelhamento: assim
	// o "_" de `QTD_ENC` ou de uma URL tipo arq_um_dois.pdf nunca vira itálico.
	const held = text
		.replace(MARK_REGEX, "")
		.replace(CODE_SPAN_REGEX, hold)
		.replace(URL_REGEX, hold);

	const converted = convert(held, 0, held.length, collectClosers(held), 0);

	return converted.replace(PLACEHOLDER_REGEX, (match, index) => {
		const atom = atoms[Number(index)];
		return atom === undefined ? match : atom;
	});
};

/**
 * Separa as cercas ```...``` multilinha do restante do texto, para o chat
 * renderizá-las como bloco monospace de verdade (o markdown-to-jsx, em modo
 * forceInline, transformaria tudo em <code> inline e o script correria junto).
 * Cerca de uma linha só continua no fluxo normal e vira code span inline.
 */
export const splitMonospaceBlocks = value => {
	const text = value === null || value === undefined ? "" : String(value);

	if (!text) return [];

	const segments = [];
	let lastIndex = 0;

	MONOSPACE_BLOCK_REGEX.lastIndex = 0;

	let match = MONOSPACE_BLOCK_REGEX.exec(text);
	while (match !== null) {
		const content = match[1];

		if (content.includes("\n")) {
			if (match.index > lastIndex) {
				segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
			}
			segments.push({ type: "block", value: content.replace(/^\r?\n/, "").replace(/\s+$/, "") });
			lastIndex = match.index + match[0].length;
		}

		match = MONOSPACE_BLOCK_REGEX.exec(text);
	}

	if (lastIndex < text.length) {
		segments.push({ type: "text", value: text.slice(lastIndex) });
	}

	return segments;
};
